<template>
  <div>
    <Stack class="component" gap="4">
      <div class="tw-flex tw-items-center tw-gap-3">
        <div class="tw-flex tw-bg-blue-100 tw-text-blue-600 tw-p-2 tw-rounded-full">
          <Icon icon="fas fa-file-upload" />
        </div>

        <h3 class="tw-font-semibold tw-mr-auto">Upload CSV</h3>

        <Button size="3" @click.prevent="showInstructions = true">
          Show instructions
        </Button>
      </div>

      <div
        class="tw-fixed tw-top-0 tw-inset-x-0 tw-flex tw-justify-center tw-z-30 tw-p-5"
      >
        <div v-if="csvErrorMsg" class="notification is-danger" role="alert">
          <button
            class="delete"
            @click="csvErrorMsg = ''"
            aria-label="Close"
          ></button>
          <p>
            {{ csvErrorMsg }}
          </p>
        </div>
      </div>

      <div class="tw-flex-grow tw-flex tw-flex-col tw-justify-between">
        <LoaderWithText v-if="csvUploadLoading">
          Processing Portfolio<span v-if="uploadName">: {{ uploadName }}</span>
        </LoaderWithText>

        <div v-else>
          <Dropzone
            :on-change="handleFileChosen"
            :multiple="false"
            class="tw-w-full tw-mb-4 stk-size-3_5"
          />

          <Stack gap="2" class="stk-size-3_5">
            <p><strong>Optional: </strong>Would you like to...</p>

            <!-- Custom Radio Button Implementation -->
            <div
              class="radio-zone"
              :class="{ 'is-selected': csvUploadTarget === 'new_member' }"
              @click="toggleOption('new_member')"
            >
              <label class="radio-option">
                <div class="custom-radio">
                  <div class="radio-inner" v-if="csvUploadTarget === 'new_member'"></div>
                </div>
                <div>
                  Upload as an account for this person 
                </div>
              </label>

              <div class="radio-option-input">
                <input
                  v-model="csvUploadNewMember"
                  class="stk-input stk-size-3"
                  :class="{ 'has-error': csvUploadTarget === 'new_member' && showValidationError }"
                  type="text"
                  placeholder="Account Name"
                  @click.stop
                />
              </div>
            </div>

            <!-- <div
              class="radio-zone"
              :class="{ 'is-selected': csvUploadTarget === 'multiple_members' }"
              @click="toggleOption('multiple_members')"
            >
              <label class="radio-option">
                <div class="custom-radio">
                  <div class="radio-inner" v-if="csvUploadTarget === 'multiple_members'"></div>
                </div>
                <div>
                  Bulk upload multiple accounts for this person
                  <small>
                    (CSV must include a column "Account" with the name of the
                    account)
                  </small>
                </div>
              </label>

              <TextLink
                class="radio-link"
                size="3"
                @click.stop="showMultipleUploadInstructions = true"
                >View Instructions</TextLink
              >
            </div> -->
          </Stack>
        </div>
      </div>
    </Stack>

    <StakeModal v-model="showInstructions">
      <HoldingsUploadInstructions />
    </StakeModal>

    <StakeModal v-model="showMultipleUploadInstructions">
      <Stack gap="2">
        <Title>Uploading to multiple members</Title>

        <p>
          To create multiple accounts, your CSV must include a column
          <code>Account</code> specifying which holdings belong to which
          account.<br />
          We will create a new account for each unique value in the <code>Account</code> column.
        </p>

        <img
          :src="`${$root.$data.STATIC_ROOT}images/portfolio/csv-upload-multiple.png`"
        />
      </Stack>
    </StakeModal>
  </div>
</template>

<script setup>
import * as Sentry from "@sentry/vue";
import { defineEmits, getCurrentInstance, onMounted, ref } from "vue";
import {
  Button,
  Dropzone,
  Icon,
  LoaderWithText,
  Stack,
  StakeModal,
  TextLink,
} from "@stakeui";
import HoldingsUploadInstructions from "./HoldingsUploadInstructions.vue";
import { awaitTask } from "@frontend/ui/features/checkup/helpers/tasks";

const props = defineProps({
  entity: {
    type: Object,
  },
  canEdit: {
    type: Boolean,
    default: false,
  },
  beforeUpload: {
    type: Function,
    default: null,
  },
});

// Allows us to use $oruga
const { proxy } = getCurrentInstance();

const emit = defineEmits(["uploaded"]);

const csrfToken = ref("");
const csvUploadLoading = ref(false);
const csvUploadTarget = ref("direct"); // Default value is "direct" (no selection)
const csvUploadNewMember = ref("");
const showInstructions = ref(false);
const showMultipleUploadInstructions = ref(false);
const uploadName = ref("");
const csvErrorMsg = ref("");
const showValidationError = ref(false);

const handleFileChosen = (files) => {
  if (files.length) {
    const defaultName = files[0].name.replace(/\.csv$/i, "");
    uploadName.value = defaultName;
    uploadFile(files);
  }
};

const validateForm = () => {
  // Clear any existing error messages
  csvErrorMsg.value = "";
  showValidationError.value = false;
  
  // Check if "Upload as an account for this person" is selected but no account name provided
  if (csvUploadTarget.value === "new_member" && !csvUploadNewMember.value.trim()) {
    csvErrorMsg.value = "Please enter an Account Name when uploading as an account for this person";
    showValidationError.value = true;
    return false;
  }
  
  return true;
};

const uploadFile = async (files) => {
  if (!props.canEdit) return;
  
  // Validate form before proceeding
  if (!validateForm()) return;

  csvUploadLoading.value = true;
  if (props.beforeUpload) {
    const canProceed = await props.beforeUpload();
    if (canProceed === false) {
      csvUploadLoading.value = false;
      return;
    }
  }
  const formData = new FormData();
  formData.append("file", files[0]);
  formData.append("name", uploadName.value);
  if (csvUploadTarget.value === "new_member") {
    formData.append(
      "account_name",
      csvUploadNewMember.value.trim()
    );
  }

  try {
    await awaitTask(
      `/api/v1/entities/${props.entity.external_id}/portfolios/upload/`,
      {
        method: "POST",
        headers: { "X-CSRFToken": csrfToken.value },
        body: formData,
      }
    );

    proxy.$oruga.notification.open({
      message: "CSV uploaded successfully",
      variant: "success",
      duration: 3000,
    });

    // Emit the uploaded event with the entity's external ID
    emit("uploaded", props.entity.external_id);
    
    // Reset component state after successful upload
    resetComponentState();
  } catch (error) {
    Sentry.captureException(error);
    csvErrorMsg.value =
      error.task?.meta?.error?.message || "Error uploading CSV";
  }
  csvUploadLoading.value = false;
};

// Function to reset the component state
const resetComponentState = () => {
  csvUploadTarget.value = "direct";
  csvUploadNewMember.value = "";
  uploadName.value = "";
  csvErrorMsg.value = "";
  showValidationError.value = false;
};

// New toggle function that allows for deselection
const toggleOption = (value) => {
  // If already selected, unselect by setting to "direct" (default)
  if (csvUploadTarget.value === value) {
    csvUploadTarget.value = "direct";
  } else {
    csvUploadTarget.value = value;
  }
  
  // Clear error message when changing selection
  csvErrorMsg.value = "";
};

onMounted(() => {
  csrfToken.value = document.querySelector(
    '[name="csrfmiddlewaretoken"]'
  )?.value;
});
</script>

<style scoped>
.radio-zone {
  display: flex;
  cursor: pointer;
  flex-direction: column;
  padding: 0.5em;
  border-radius: 0.5em;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color;

  &:hover {
    background-color: var(--stk-primary-95);
  }

  &.is-selected {
    background-color: var(--stk-primary-95);
    box-shadow: 0 0 0 0.125em var(--stk-primary);
  }
}

.radio-option {
  cursor: pointer;
  display: flex;
  gap: 0.5em;
  align-items: start;

  small {
    display: block;
    opacity: 0.7;
  }
}

.radio-option-input,
.radio-link {
  display: flex;
  margin-left: 1.25rem;
  margin-top: 0.25rem;
}

/* Custom radio button styling */
.custom-radio {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid var(--stk-primary);
  position: relative;
  margin-top: 0.1875em;
}

.radio-inner {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--stk-primary);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.has-error {
  border-color: var(--stk-danger) !important;
  box-shadow: 0 0 0 0.125em rgba(var(--stk-danger-rgb), 0.25) !important;
}
</style>
